import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import AlertsComponent from 'components/Alert/AlertsComponent';
import { TypographyComponent as TypographyElement, Radio, RadioGroup, GridRow, GridItem } from '@tvg/fuse-design';
import OptionButton from './OptionButton/OptionButton';
import ShowMoreOption from './ShowMoreOption';
import { getArticle } from '../../utilities/util';
import { OptionSelectorProps } from './OptionSelectorProps';

const Container = styled.div<{ isRadioOption: boolean }>`
  ${({ isRadioOption }) => !isRadioOption && 'margin-bottom: var(--spacing-space16);'}
`;

const OptionGridItem = styled(GridItem)<{ isExpanded: boolean; xxs: number }>`
  ${({ isExpanded }) =>
    !isExpanded &&
    `      
      &:not(:nth-child(-n + 12)) {
        display: none;
      } 
  `};

  @media (max-width: calc(20rem - 1px)) {
    ${({ xxs: numberOfColumns }) => {
      switch (numberOfColumns) {
        case 3:
          return 'grid-column: auto / span 4;';
        case 4:
          return 'grid-column: auto / span 6;';
        case 6:
          return 'grid-column: auto / span 12;';
        default:
          return '';
      }
    }}
  }
`;

const OptionRadio = styled(Radio)`
  label {
    font-size: var(--font-size-m1);
  }
`;

function OptionSelector({ skus, formattedName, name }: OptionSelectorProps) {
  const isColourOption = name === 'colour';

  const {
    isOptionSelectorError,
    setOptionSelectorError,
    selectedSku,
    uniqueSKUList,
    selectedOptions,
    handleOptionSelect,
  } = useSelectedSkuContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    message: `Please select ${getArticle(formattedName)}`,
    isShown: false,
  });

  const optionLengths = useMemo(() => {
    const lengths = {
      extraLarge: false,
      large: false,
      medium: false,
    };

    uniqueSKUList[name]?.some((option) => {
      const { length } = option.value;

      if (length >= 20) {
        lengths.extraLarge = true;
        return true;
      }

      if (length >= 12) {
        lengths.large = true;
      }

      if (length >= 6) {
        lengths.medium = true;
      }

      return false;
    });

    return lengths;
  }, [uniqueSKUList, name]);

  const numberOfColumns = useMemo(() => {
    if (isColourOption || optionLengths.large) {
      return 6;
    }

    if (optionLengths.medium) {
      return 4;
    }

    return 3;
  }, [isColourOption, optionLengths]);

  const isRadioOption = !isColourOption && optionLengths.extraLarge;

  /**
   * Finds the first non-empty swatch for a given option value.
   *
   * This function iterates over the list of SKUs and returns the swatches of the first SKU
   * that matches the provided option value and has non-empty swatches.
   *
   * @param {string} value - The value of the option to match against the SKUs.
   * @returns {object | undefined} - The swatches of the first matching SKU, or undefined if no matching SKU is found.
   *
   */
  const findSwatches = (value: string) => {
    const matchingSku = skus.find(
      (sku) =>
        sku.options[name as keyof typeof sku.options] === value &&
        sku.swatches &&
        Object.keys(sku.swatches).length > 0 &&
        (sku.swatches.colour || sku.swatches.image),
    );

    return matchingSku ? matchingSku.swatches : undefined;
  };

  const handleSelectedSku = (sku: { value: string; disabled: boolean }) => {
    setErrorMessage({
      ...errorMessage,
      isShown: false,
    });

    handleOptionSelect(sku.value, name);
  };

  const showError = () => isOptionSelectorError.value && errorMessage.isShown;

  const renderError = () => (
    <GridRow>
      <GridItem data-testid={`option-selector-error-${name}`} xxs={12}>
        <AlertsComponent errorMessage={errorMessage.message} />
      </GridItem>
    </GridRow>
  );

  useEffect(() => {
    const isOptionSelected = Object.keys(selectedOptions).some((key) => key === name);

    setErrorMessage({
      ...errorMessage,
      isShown: !isOptionSelected,
    });
  }, [isOptionSelectorError]);

  useEffect(() => {
    if (selectedSku) setOptionSelectorError({ value: false });
  }, [selectedSku]);

  const underscoresSlashesSpacesPattern = /[_/\s]/g;

  const renderOptionButtons = () => (
    <GridRow>
      {uniqueSKUList &&
        uniqueSKUList[name]?.map((sku) => (
          <OptionGridItem xxs={numberOfColumns} key={sku.value} isExpanded={isExpanded}>
            <OptionButton
              id={`${name}-${sku.value}`.replace(underscoresSlashesSpacesPattern, '-').toLowerCase()}
              name={name}
              label={sku.value}
              checked={(uniqueSKUList && selectedOptions[name]) === sku.value}
              disabled={sku.disabled}
              swatchColour={isColourOption ? findSwatches(sku.value)?.colour : undefined}
              swatchImage={isColourOption ? findSwatches(sku.value)?.image : undefined}
              handleOnClick={() => handleSelectedSku(sku)}
              key={sku.value}
            />
          </OptionGridItem>
        ))}
    </GridRow>
  );

  const renderRadioOptions = () => (
    <RadioGroup dataTestId="radio-group" groupName="radio-group">
      {uniqueSKUList &&
        uniqueSKUList[name]?.map((sku) => (
          <OptionRadio
            type="checkbox"
            dataTestId={`radio-button-${sku.value.split(' ').join('-')}`}
            clickCallback={() => handleSelectedSku(sku)}
            radioLabel={sku.value}
            radioName={sku.value}
            disabled={sku.disabled}
            key={sku.value}
            checked={(uniqueSKUList && selectedOptions[name]) === sku.value}
            defaultChecked={(uniqueSKUList && selectedOptions[name]) === sku.value}
          />
        ))}
    </RadioGroup>
  );

  return (
    <div data-testid={`option-selector-${name}`.replace(underscoresSlashesSpacesPattern, '-').toLowerCase()}>
      <Container isRadioOption={isRadioOption}>
        <GridRow>
          <GridItem xxs={12}>
            <TypographyElement
              id={`option-selector-label-${name}`}
              size="1"
              tag="p"
              color={showError() ? 'var(--color-universal-redMid)' : undefined}
            >
              Pick your {formattedName}
            </TypographyElement>
          </GridItem>
        </GridRow>

        {showError() ? renderError() : null}

        {isRadioOption ? renderRadioOptions() : renderOptionButtons()}

        {uniqueSKUList && uniqueSKUList[name]?.length > 12 && (
          <ShowMoreOption isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
        )}
      </Container>
    </div>
  );
}

export default OptionSelector;
